(() => {
  const CATEGORY_START_AGE = { adult: 30, child_with_bed: 7, child_without_bed: 7, infant: 1 };
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const dateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseDate = (value) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null;
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const formatDate = (date) => date.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
  const monthIndex = (date) => (date.getFullYear() * 12) + date.getMonth();
  class CustomCalendar {
    constructor(container, onChange) {
      this.container = container;
      this.mode = container.dataset.calendarMode || 'departure';
      this.category = container.dataset.calendarCategory || '';
      this.blockedDates = [];
      this.allowedTill = null;

      if (this.mode === 'departure') {
        try {
          this.blockedDates = JSON.parse(container.dataset.blockedDates || '[]');
          if (!Array.isArray(this.blockedDates)) this.blockedDates = [];
        } catch (error) {
          this.blockedDates = [];
        }

        this.allowedTill = parseDate(container.dataset.allowedTill || '');
      }
      this.trigger = container.querySelector('[data-at-calendar-trigger]');
      this.label = container.querySelector('[data-at-calendar-label]');
      this.input = container.querySelector('[data-at-calendar-input]');
      this.panel = container.querySelector('[data-at-calendar-panel]');
      this.days = container.querySelector('[data-at-calendar-days]');
      this.previous = container.querySelector('[data-at-calendar-previous]');
      this.next = container.querySelector('[data-at-calendar-next]');
      this.monthSelect = container.querySelector('[data-at-calendar-month]');
      this.yearSelect = container.querySelector('[data-at-calendar-year]');
      this.onChange = onChange;
      this.placeholder = null;
      this.backdrop = null;
      this.handleOutsideClick = (event) => {
        if (this.container.contains(event.target) || this.panel.contains(event.target)) return;
        this.close();
      };
      this.handleViewportChange = () => this.positionPanel();
      this.today = new Date();
      this.today.setHours(0, 0, 0, 0);
      if (this.mode === 'dob') {
        const maxAgeYears = this.category === 'infant' ? 1 : this.category === 'child' ? 11 : 100;
        this.minimum = new Date(this.today);
        this.minimum.setFullYear(this.today.getFullYear() - maxAgeYears);
      } else {
        this.minimum = new Date(this.today);
      }
      this.maximum = this.mode === 'dob'
        ? new Date(this.today)
        : this.allowedTill && this.allowedTill >= this.today
          ? new Date(this.allowedTill)
          : new Date(this.today.getFullYear() + 20, 11, 31);
      if (this.mode === 'field') {
        this.minimum = parseDate(this.input.min) || new Date(this.today);
        this.maximum = parseDate(this.input.max) || new Date(this.today.getFullYear() + 20, 11, 31);
      }
      this.selected = parseDate(this.container.dataset.calendarValue || this.input?.value);

      const startAge = CATEGORY_START_AGE[this.category] || 0;
      const startDate = this.mode === 'dob'
        ? new Date(this.today.getFullYear() - startAge, this.today.getMonth(), 1)
        : this.mode === 'expiry'
          ? new Date(this.today.getFullYear() + 1, this.today.getMonth(), 1)
          : new Date(this.minimum.getFullYear(), this.minimum.getMonth(), 1);
      this.calendarMonth = this.selected ? new Date(this.selected.getFullYear(), this.selected.getMonth(), 1) : startDate;

      if (!this.trigger || !this.input || !this.panel || !this.days) return;
      this.populateSelectors();
      this.bind();
      if (this.selected) {
        this.label.textContent = formatDate(this.selected);
        this.trigger.classList.add('has-value');
      }
      this.render();
    }

    populateSelectors() {
      this.monthSelect.innerHTML = MONTHS.map((month, index) => `<option value="${index}">${month}</option>`).join('');
      const firstYear = this.minimum.getFullYear();
      const lastYear = this.maximum.getFullYear();
      const years = [];
      if (this.mode === 'dob') {
        for (let year = lastYear; year >= firstYear; year -= 1) years.push(year);
      } else {
        for (let year = firstYear; year <= lastYear; year += 1) years.push(year);
      }
      this.yearSelect.innerHTML = years.map((year) => `<option value="${year}">${year}</option>`).join('');
    }

    bind() {
      this.trigger.addEventListener('click', () => this.panel.hidden ? this.open() : this.close());
      this.panel.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') { event.preventDefault(); this.close(); this.trigger.focus(); }
        if (event.key === 'Tab') {
          const controls = [...this.panel.querySelectorAll('button:not(:disabled), select:not(:disabled)')];
          const first = controls[0], last = controls[controls.length - 1];
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        }
      });
      this.previous.addEventListener('click', () => this.move(-1));
      this.next.addEventListener('click', () => this.move(1));
      this.monthSelect.addEventListener('change', () => {
        this.calendarMonth = new Date(Number(this.yearSelect.value), Number(this.monthSelect.value), 1);
        this.keepMonthInRange();
        this.render();
      });
      this.yearSelect.addEventListener('change', () => {
        this.calendarMonth = new Date(Number(this.yearSelect.value), Number(this.monthSelect.value), 1);
        this.keepMonthInRange();
        this.render();
      });
      this.container.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.close();
          this.trigger.focus();
        }
      });
    }

    keepMonthInRange() {
      const current = monthIndex(this.calendarMonth);
      const minimum = monthIndex(this.minimum);
      const maximum = monthIndex(this.maximum);
      if (current < minimum) this.calendarMonth = new Date(this.minimum.getFullYear(), this.minimum.getMonth(), 1);
      if (current > maximum) this.calendarMonth = new Date(this.maximum.getFullYear(), this.maximum.getMonth(), 1);
    }

    open() {
      if (this.input.disabled || this.input.closest('fieldset:disabled')) return;
      if (this.mode === 'field') {
        this.minimum = parseDate(this.input.min) || new Date(this.today);
        this.maximum = parseDate(this.input.max) || new Date(this.today.getFullYear() + 20, 11, 31);
        this.populateSelectors();
        this.keepMonthInRange();
      }
      if (CustomCalendar.active && CustomCalendar.active !== this) CustomCalendar.active.close();
      CustomCalendar.active = this;
      this.placeholder = document.createComment('Adventure Tales calendar');
      this.panel.parentNode.insertBefore(this.placeholder, this.panel);
      this.backdrop = document.createElement('button');
      this.backdrop.type = 'button';
      this.backdrop.className = 'at-date-picker__backdrop';
      this.backdrop.setAttribute('aria-label', 'Close calendar');
      this.backdrop.addEventListener('click', () => this.close());
      document.body.appendChild(this.backdrop);
      document.body.appendChild(this.panel);
      this.panel.classList.add('at-date-picker__panel--portaled');
      this.panel.hidden = false;
      this.container.classList.add('is-open');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.render();
      this.positionPanel();
      this.panel.setAttribute('aria-modal', 'true');
      (this.days.querySelector('.is-selected:not(:disabled)') || this.days.querySelector('button:not(:disabled)') || this.monthSelect).focus();
      document.addEventListener('pointerdown', this.handleOutsideClick);
      window.addEventListener('resize', this.handleViewportChange);
      window.addEventListener('scroll', this.handleViewportChange, true);
    }

    close() {
      if (!this.panel) return;
      this.panel.hidden = true;
      this.panel.classList.remove('at-date-picker__panel--portaled');
      this.panel.removeAttribute('style');
      if (this.placeholder?.parentNode) {
        this.placeholder.parentNode.insertBefore(this.panel, this.placeholder);
        this.placeholder.remove();
      }
      this.placeholder = null;
      this.backdrop?.remove();
      this.backdrop = null;
      this.container.classList.remove('is-open');
      this.trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('pointerdown', this.handleOutsideClick);
      window.removeEventListener('resize', this.handleViewportChange);
      window.removeEventListener('scroll', this.handleViewportChange, true);
      if (CustomCalendar.active === this) CustomCalendar.active = null;
    }

    positionPanel() {
      if (this.panel.hidden || !this.panel.classList.contains('at-date-picker__panel--portaled')) return;
      this.panel.style.top = '50%';
      this.panel.style.left = '50%';
      this.panel.style.transform = 'translate(-50%, -50%)';
    }

    move(amount) {
      this.calendarMonth = new Date(this.calendarMonth.getFullYear(), this.calendarMonth.getMonth() + amount, 1);
      this.keepMonthInRange();
      this.render();
    }

    disabled(date) {
      const value = dateKey(date);

      return (
        date < this.minimum ||
        date > this.maximum ||
        (this.mode === 'departure' && this.allowedTill && date > this.allowedTill) ||
        (this.mode === 'departure' && this.blockedDates.includes(value))
      );
    }

    choose(date) {
      if (this.disabled(date)) return;

      this.selected = date;
      const value = dateKey(date);
      this.container.dataset.calendarValue = value;
      this.input.value = value;
      this.input.setAttribute('value', value);
      this.label.textContent = formatDate(date);
      this.trigger.classList.add('has-value');
      this.close();
      this.render();
      if (this.onChange) this.onChange(date, this);
      this.trigger.focus();
    }

    render() {
      const year = this.calendarMonth.getFullYear();
      const month = this.calendarMonth.getMonth();
      const firstWeekday = new Date(year, month, 1).getDay();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const currentMonthIndex = monthIndex(this.calendarMonth);
      this.monthSelect.value = String(month);
      this.yearSelect.value = String(year);
      this.previous.disabled = currentMonthIndex <= monthIndex(this.minimum);
      this.next.disabled = currentMonthIndex >= monthIndex(this.maximum);
      this.days.innerHTML = '';

      for (let blank = 0; blank < firstWeekday; blank += 1) {
        const spacer = document.createElement('span');
        spacer.className = 'at-date-picker__blank';
        spacer.setAttribute('aria-hidden', 'true');
        this.days.appendChild(spacer);
      }

      for (let day = 1; day <= daysInMonth; day += 1) {
        const date = new Date(year, month, day);
        const button = document.createElement('button');
        const selected = this.selected && dateKey(date) === dateKey(this.selected);
        button.type = 'button';
        button.textContent = String(day);
        button.disabled = this.disabled(date);
        button.setAttribute('role', 'gridcell');
        button.setAttribute('aria-label', date.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }));
        if (dateKey(date) === dateKey(this.today)) button.classList.add('is-today');
        if (selected) {
          button.classList.add('is-selected');
          button.setAttribute('aria-selected', 'true');
        }
        button.addEventListener('click', () => this.choose(date));
        this.days.appendChild(button);
      }
    }
  }

  window.ATCustomCalendar = CustomCalendar;
  let sequence = 0;
  const enhance = (input) => {
    if (input.dataset.atDateEnhanced || input.closest('[data-at-calendar]')) return;
    input.dataset.atDateEnhanced = 'true';
    const labels = [...(input.labels || [])];
    const title = labels.map(label => label.textContent.trim()).join(' ') || input.getAttribute('aria-label') || 'Select date';
    const wrapper = document.createElement('span');
    wrapper.className = 'at-date-picker at-date-picker--shared';
    wrapper.dataset.atCalendar = '';
    wrapper.dataset.calendarMode = 'field';
    const panelId = `ATDatePanel-${++sequence}`;
    wrapper.innerHTML = `<button class="at-date-picker__trigger" type="button" aria-haspopup="dialog" aria-expanded="false" aria-controls="${panelId}" data-at-calendar-trigger><span data-at-calendar-label>dd/mm/yyyy</span><span aria-hidden="true">▣</span></button><span class="at-date-picker__panel" id="${panelId}" role="dialog" data-at-calendar-panel hidden><span class="at-date-picker__header"><button type="button" aria-label="Previous month" data-at-calendar-previous>←</button><span class="at-date-picker__jump"><select data-at-calendar-month aria-label="Month"></select><select data-at-calendar-year aria-label="Year"></select></span><button type="button" aria-label="Next month" data-at-calendar-next>→</button></span><span class="at-date-picker__weekdays" aria-hidden="true"><span>Su</span><span>Mo</span><span>Tu</span><span>We</span><span>Th</span><span>Fr</span><span>Sa</span></span><span class="at-date-picker__days" role="grid" data-at-calendar-days></span><button type="button" class="at-date-picker__dismiss">Close calendar</button></span>`;
    input.before(wrapper);
    wrapper.append(input);
    input.hidden = true;
    input.dataset.atCalendarInput = '';
    const trigger = wrapper.querySelector('[data-at-calendar-trigger]');
    trigger.id = `${panelId}-trigger`;
    trigger.setAttribute('aria-label', title);
    wrapper.querySelector('[data-at-calendar-panel]').setAttribute('aria-label', title);
    labels.forEach(label => { if (label.htmlFor === input.id) label.htmlFor = trigger.id; });
    const calendar = new CustomCalendar(wrapper, () => {
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      trigger.setAttribute('aria-label', `${title}: ${calendar.label.textContent}`);
    });
    wrapper.querySelector('.at-date-picker__dismiss').addEventListener('click', () => { calendar.close(); trigger.focus(); });
    input.addEventListener('invalid', event => { event.preventDefault(); trigger.focus(); calendar.open(); });
    const syncDisabled = () => { trigger.disabled = input.disabled || Boolean(input.closest('fieldset:disabled')); };
    new MutationObserver(syncDisabled).observe(input, { attributes: true, attributeFilter: ['disabled'] });
    const fieldset = input.closest('fieldset');
    if (fieldset) new MutationObserver(syncDisabled).observe(fieldset, { attributes: true, attributeFilter: ['disabled'] });
    syncDisabled();
    input.form?.addEventListener('reset', () => setTimeout(() => {
      calendar.selected = parseDate(input.value);
      wrapper.dataset.calendarValue = input.value;
      calendar.label.textContent = calendar.selected ? formatDate(calendar.selected) : 'dd/mm/yyyy';
      trigger.classList.toggle('has-value', Boolean(calendar.selected));
      trigger.setAttribute('aria-label', title);
      calendar.close();
      calendar.render();
    }, 0));
  };
  const scan = scope => {
    if (scope.matches?.('input[type="date"]')) enhance(scope);
    scope.querySelectorAll?.('input[type="date"]').forEach(enhance);
  };
  const start = () => {
    scan(document);
    new MutationObserver(records => records.forEach(record => record.addedNodes.forEach(node => {
      if (node.nodeType === 1) scan(node);
    }))).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
